import * as React from 'react'
import { Check, Crosshair, Trash2, TriangleAlert, X } from 'lucide-react'
import type { TreeDto, TreeJobDto } from '@lib/dto/tree.dto'
import {
  MAX_ESTIMATED_MINUTES,
  formatMinutesLong,
  parseMinutes,
} from '@lib/helpers/minutes'
import type { JobLevel, JobType } from '@lib/enums/job-type.enum'
import {
  JOB_LEVELS,
  JOB_TYPES,
  JOB_TYPE_LABELS,
} from '@lib/enums/job-type.enum'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import type { TreeSpeciesDto } from '@lib/dto/tree-species.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import { JOB_TYPE_BG_CLASSES } from '@/field-map/markerStyles'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { SurveyPhotos } from '@/components/SurveyPhotos'
import { TreeProblems } from '@/field-map/TreeProblems'
import { SurveyVideos } from '@/components/SurveyVideos'
import { useVideoAvailability } from '@/hooks/useVideos'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { solutionMinutes } from '@lib/helpers/solution-pricing'
import { isPartialQuantity, parseQuantity } from '@lib/helpers/quantity'
import { itemizedMinutes } from '@lib/helpers/job-time'
import { jobTypeHasLevel } from '@lib/enums/job-type.enum'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { formatMoney, parseMoney, priceMinutes } from '@lib/helpers/money'
import { PriceOverride } from '@/field-map/PriceOverride'
import { cn } from '@/lib/utils'
import type { TreePrice } from '@/bid-total/useTreeBidTotal'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import type { ChangeQuestion } from '@/site-visits/siteRecordChanges'
import {
  TreeLastVisit,
  type TreeLastVisitProps,
} from '@/field-map/TreeLastVisit'
import { RecordHistory } from '@/site-visits/RecordHistory'
import { SEASON_LABELS, type Season } from '@lib/enums/season.enum'
import { HAZARD_SEVERITY } from '@lib/helpers/proposal-plans'
import { SeasonPicker } from '@/field-map/SeasonPicker'

/**
 * Controls are 44px on a phone and back to the app's usual density from `lg`.
 *
 * On a phone someone is standing in a park, in sun, holding the device in one
 * hand and possibly wearing a glove — density is the right default for a data
 * grid and the wrong one for a field tool. At a desk none of that applies, and
 * a column of 44px controls in a 22rem inspector is a form built for a thumb
 * that no thumb will ever touch.
 */
const FIELD_CONTROL = 'h-11 lg:h-9'

/**
 * Where the editor is mounted.
 *
 * `sheet` docks it to the bottom of a phone screen; `inspector` puts it in a
 * column beside the map on a desktop. Same component either way — the fields
 * and their behavior do not change, only the frame around them and how the
 * species chips wrap inside it.
 */
export type TreeSheetVariant = 'sheet' | 'inspector'

/** A block of what was found: a bare fieldset, dimmed when it is disabled. */
const FINDINGS = 'm-0 min-w-0 space-y-4 border-0 p-0 disabled:opacity-60'

/** A block of what the tree is, locked the same way on an earlier visit. */
const RECORD = FINDINGS

/**
 * Whether the tree carries pruning work, which is what its season is for. A
 * tree marked "No pruning recommended" has a zero estimate that is an answer
 * rather than work, so only a removal quote counts on one.
 */
function hasPruningWork(tree: TreeDto): boolean {
  const prunes =
    !tree.noPruningRecommended &&
    (tree.jobs.length > 0 ||
      tree.estimatedLaborMinutes !== null ||
      tree.pruningPriceOverride !== null)
  return prunes || tree.removalOffered
}

/** Whether any price on the tree, or on one of its problems, was set by hand. */
function hasPriceOverride(tree: TreeDto): boolean {
  return (
    tree.pruningPriceOverride !== null ||
    tree.removalPriceOverride !== null ||
    tree.problems.some((problem) => problem.priceOverride !== null)
  )
}

/**
 * Whether a serious problem on the tree has work set for a later season.
 *
 * Severity 4 and up is a credible risk to people or property (the plans'
 * "hazard"), and a proposal puts work in a later season out of what the
 * client approves now. Said while the arborist can still change it.
 */
function hazardWaits(tree: TreeDto, current: Season): boolean {
  const serious = tree.problems.some(
    (problem) => (problem.severity ?? 0) >= HAZARD_SEVERITY,
  )
  if (!serious) return false
  const later = (season: Season | null) => season !== null && season !== current
  return (
    (hasPruningWork(tree) && later(tree.pruningSeason)) ||
    tree.problems.some((problem) =>
      problem.solutions.length === 0
        ? later(problem.season)
        : problem.solutions.some((solution) => later(solution.season)),
    )
  )
}

/** Why what was found cannot be changed here, and what to do about it. */
function findingsNote(loading: boolean, canEditRecord: boolean): string {
  if (loading) {
    return canEditRecord
      ? 'Loading what this visit found. What this tree is can still be changed.'
      : 'Loading what this visit found.'
  }
  return canEditRecord
    ? 'Work and problems belong to a visit. Start one above to record them; what this tree is can still be changed.'
    : 'What this visit found, as it was found. Choose Edit it above to change it.'
}

/** Whether a tree's price says anything worth a place in the header. */
function pricesAnything(price: TreePrice | null): price is TreePrice {
  return (
    price !== null &&
    (price.cents > 0 ||
      price.later !== null ||
      price.removalAlternativeCents !== null)
  )
}

/** "This tree $340", in the header beside the close button. */
function TreePriceFigure({ price }: { price: TreePrice | null }) {
  if (!pricesAnything(price)) return null
  return (
    <span className="flex items-baseline gap-1 text-sm">
      <span className="text-xs text-muted-text">This tree</span>
      <span
        className={cn(
          'font-semibold tabular-nums',
          price.isOptional && 'text-secondary-text',
        )}
      >
        {formatMoney(price.cents)}
      </span>
    </span>
  )
}

/**
 * What the figure leaves out: optional work the headline does not count, a
 * removal offered instead, work in a later season. One quiet line under the
 * header, and only when there is something to say.
 */
function TreePriceNotes({ price }: { price: TreePrice | null }) {
  if (!pricesAnything(price)) return null
  const notes: string[] = []
  if (price.isOptional) notes.push('Optional, not in the bid total')
  if (price.removalAlternativeCents !== null) {
    notes.push(`or ${formatMoney(price.removalAlternativeCents)} to remove it`)
  }
  if (price.later !== null) {
    notes.push(
      `${formatMoney(price.later.cents)} more in ${SEASON_LABELS[price.later.season].toLowerCase()}`,
    )
  }
  if (notes.length === 0) return null
  return (
    <p className="w-full truncate text-right text-xs text-muted-text">
      {notes.join(' · ')}
    </p>
  )
}

export interface TreeSheetProps {
  tree: TreeDto
  /**
   * The species available to choose from — the live catalog, in order.
   *
   * Passed in rather than fetched here because ninety of these can mount over
   * one survey session, and every one would re-run the same query.
   */
  species: TreeSpeciesDto[]
  /** The problem directory, archived entries included — see `TreeProblems`. */
  problemTypes: TreeProblemTypeDto[]
  /** The operator's rule: every pruning job needs a time. */
  requireItemizedEstimates: boolean
  /**
   * The crew rate, for treatments charged by time with no override.
   *
   * A prop rather than a query of its own, so the sheet keeps taking all its
   * data from the page — which is what lets the map render it offline.
   */
  ratePerHour: string | null
  variant?: TreeSheetVariant
  /** The tree sits outside the site's drawn boundary. Advisory only. */
  outsideBoundary: boolean
  /**
   * The season it is now, by this device's date and the organization's
   * months. New pruning work starts in it (docs/seasonal-work.md).
   */
  currentSeason: Season
  /**
   * Whether the browser has a connection.
   *
   * Trees are captured offline and replayed from an outbox; photos are not, so
   * the photo control has to be able to say why it is unavailable rather than
   * failing after somebody has taken one.
   */
  isOnline: boolean
  /** This tree has queued work, so the server may not know it exists yet. */
  isPendingSync: boolean
  /**
   * Whether what was found on the tree can be recorded here: its work, time,
   * removal, problems and photos are a visit's, and there must be one being
   * worked in (see `canRecordIn`). What the tree is (its label, species,
   * diameter, notes and pin) can always be changed.
   */
  canRecordFindings: boolean
  /**
   * Findings could be recorded, but this visit's have not loaded yet: says so
   * rather than asking for a visit there already is.
   */
  findingsLoading?: boolean
  /** The visit being worked in, which the tree's photos and videos are of. */
  visitId: string | null
  /**
   * Whether what the tree is (its label, species, diameter, notes and pin)
   * can be changed here. False while an earlier visit is shown: the site
   * record is only ever changed as it stands now (docs/site-visits.md).
   */
  canEditRecord?: boolean
  /**
   * What the visit before this one found on the tree, on a revisit; shown
   * only while findings can be recorded, since Still here records them.
   */
  lastVisit?: TreeLastVisitProps | null
  /**
   * What this tree comes to on the bid, priced as the proposal prices it.
   * Shown in the header so the cost of an edit sits beside the edit.
   */
  price?: TreePrice | null
  onChange: (tree: TreeDto) => void
  /** Hand off to move mode, which swaps this sheet for the compact move bar. */
  onMove: () => void
  /**
   * Take the tree off the site, with the answer to "Gone, or recorded by
   * mistake?" when `removeQuestion` asked it: true for the mistake.
   */
  onDelete: (isCorrection?: boolean, changedOn?: string) => void
  /** What deleting asks on a revisit; left out, it asks nothing. */
  removeQuestion?: ChangeQuestion | null
  /** Ask when it went too: for a tree taken off in no visit. */
  asksWhen?: boolean
  onClose: () => void
}

/**
 * The editor for one tree, docked to the bottom of the screen.
 *
 * Deliberately NOT a modal dialog. This panel shares the screen with a live
 * map — the person can pan and zoom while it is open — so a focus trap and a
 * scrim, everything a dialog exists to do, would work against it. Repositioning
 * hands off to the compact move bar rather than happening here, because at 68%
 * of the screen this sheet would cover the pin being dragged.
 *
 * The caller keys this component on the tree's id, so selecting a different
 * tree remounts it and every piece of local state — the half-typed hours, the
 * armed delete confirmation — resets on its own. That is cheaper and harder to
 * get wrong than an effect that has to remember to clear each one.
 */
export function TreeSheet({
  tree,
  species,
  problemTypes,
  ratePerHour,
  requireItemizedEstimates,
  variant = 'sheet',
  outsideBoundary,
  isOnline,
  isPendingSync,
  canRecordFindings,
  findingsLoading = false,
  visitId,
  canEditRecord = true,
  lastVisit = null,
  price = null,
  currentSeason,
  onChange: onTreeChange,
  onMove,
  onDelete,
  removeQuestion = null,
  asksWhen = false,
  onClose,
}: TreeSheetProps) {
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)
  const videoAvailability = useVideoAvailability()

  /**
   * Every change to the tree goes out through here, so pruning work never
   * leaves the sheet without a season: the first job, estimate or removal
   * recorded on a tree starts it in the current season. Doing it at the one
   * exit rather than in each handler means a new control cannot forget to.
   */
  function onChange(next: TreeDto): void {
    const seasoned =
      next.pruningSeason === null && hasPruningWork(next)
        ? { ...next, pruningSeason: currentSeason }
        : next
    /*
     * The reason for a price set by hand goes with the last such price, so a
     * note nobody can see does not come back explaining a price nobody set.
     */
    onTreeChange(
      hasPriceOverride(seasoned) || seasoned.priceOverrideNotes === null
        ? seasoned
        : { ...seasoned, priceOverrideNotes: null },
    )
  }

  /**
   * The estimate field holds text, not the committed number.
   *
   * Deriving the input's value from the tree on every render would fight the
   * person typing — a field that is briefly empty between "9" and "90" would
   * commit a clear and then be re-rendered from it. Local text lets a
   * half-typed value exist; only a well-formed one reaches the tree.
   */
  const [minutesText, setMinutesText] = React.useState(
    tree.estimatedLaborMinutes === null
      ? ''
      : String(tree.estimatedLaborMinutes),
  )

  /** Local text for the same reason the estimate keeps its own. */
  const [dbhText, setDbhText] = React.useState(tree.dbhInches ?? '')

  const [removalMinutesText, setRemovalMinutesText] = React.useState(
    tree.removalMinutes === null ? '' : String(tree.removalMinutes),
  )

  /**
   * Turn removal on or off.
   *
   * Switching it off clears the figures rather than keeping them out of
   * sight: a removal time nobody can see is one that comes back the next time
   * somebody ticks the box, and surprises them with a price they did not set.
   */
  function setRemovalOffered(offered: boolean): void {
    if (!offered) setRemovalMinutesText('')
    onChange({
      ...tree,
      removalOffered: offered,
      removalMinutes: offered ? tree.removalMinutes : null,
      removalNotes: offered ? tree.removalNotes : null,
      removalPriceOverride: offered ? tree.removalPriceOverride : null,
    })
  }

  /**
   * Mark the tree "No pruning recommended", or take the mark off.
   *
   * Marking it clears the jobs, sets the time to zero and drops a pruning
   * price, which is what the server writes beside the mark anyway: the tree
   * says one thing. Taking it off leaves the time empty, so the tree goes back
   * to needing an estimate rather than keeping a zero nobody typed.
   */
  function setNoPruning(marked: boolean): void {
    setMinutesText(marked ? '0' : '')
    onChange({
      ...tree,
      noPruningRecommended: marked,
      jobs: marked ? [] : tree.jobs,
      estimatedLaborMinutes: marked ? 0 : null,
      pruningPriceOverride: null,
    })
  }

  /** Only a well-formed figure is committed, as everywhere else here. */
  function commitRemovalMinutes(raw: string): void {
    setRemovalMinutesText(raw)
    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange({ ...tree, removalMinutes: null })
      return
    }
    const minutes = parseMinutes(trimmed)
    if (minutes === null) return
    onChange({ ...tree, removalMinutes: minutes })
  }

  /**
   * A value that can never become valid, as opposed to one mid-typing.
   * "14." is on its way somewhere; "14.567" is not.
   */
  const dbhIsUnreadable =
    dbhText.trim() !== '' && !isPartialQuantity(dbhText)

  const itemizedTotal = itemizedMinutes(
    tree.jobs.map((job) => ({ type: job.type, minutes: job.minutes })),
  )
  const otherJob = tree.jobs.find((job) => job.type === 'OTHER')
  const someItemized = tree.jobs.some((job) => job.minutes !== null)
  const partiallyItemized = someItemized && itemizedTotal === null

  const centsPerHour = parseMoney(ratePerHour)
  const pruningMinutes = itemizedTotal ?? tree.estimatedLaborMinutes
  /** What the formula charges for the pruning: its minutes at the crew rate. */
  const pruningFormulaCents =
    centsPerHour === null || pruningMinutes === null
      ? null
      : priceMinutes(pruningMinutes, centsPerHour)
  const removalFormulaCents =
    centsPerHour === null || tree.removalMinutes === null
      ? null
      : priceMinutes(tree.removalMinutes, centsPerHour)
  /** What the formula priced nothing for, said instead of a figure. */
  function unpricedNote(minutes: number | null): string {
    if (minutes === null) return 'add a time first'
    return 'no tree labor rate is set'
  }

  /** Minutes every chosen treatment adds, across every problem on the tree. */
  const solutionMinutesTotal = solutionMinutes(
    tree.problems.flatMap((problem) => problem.solutions),
  )

  /**
   * Commit a typed diameter, or clear it.
   *
   * Empty is "not measured", which is a real state: plenty of trees are
   * surveyed without a tape, and a treatment priced by diameter simply does
   * not price until one comes out.
   */
  function commitDbh(raw: string): void {
    setDbhText(raw)
    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange({ ...tree, dbhInches: null })
      return
    }
    /*
     * Only a well-formed value is committed, exactly as for the estimate
     * above — and here it matters more than tidiness. "14." is what every
     * decimal passes through on its way to "14.5", the outbox flushes 800ms
     * after the last keystroke, and a tree write is validated as ONE batch:
     * a single value the server rejects fails every other queued edit for the
     * site with it, non-retriably. Local text holds the half-typed value; only
     * a parseable one reaches the tree.
     */
    const parsed = parseQuantity(trimmed)
    if (parsed === null) return
    onChange({ ...tree, dbhInches: parsed })
  }

  /**
   * Identify the tree, or clear it back to unidentified.
   *
   * The mark is written alongside the id because the pin draws from it — the
   * map re-renders from this object, not from a refetch, so a pin whose color
   * waited for the network would lag every tap by a round trip.
   */
  function setSpecies(chosen: TreeSpeciesDto | null): void {
    onChange({
      ...tree,
      speciesId: chosen?.id ?? null,
      species:
        chosen === null
          ? null
          : { name: chosen.name, shape: chosen.shape, color: chosen.color },
    })
  }

  /**
   * Toggle a job type on or off. A new job starts at Level 1 — the level of
   * every badge on the reference maps, so it is the answer that needs no
   * thought in the field.
   */
  function toggleJob(type: JobType): void {
    const existing = tree.jobs.find((job) => job.type === type)
    const jobs: TreeJobDto[] =
      existing === undefined
        ? [...tree.jobs, { type, level: 1, minutes: null, notes: null }]
        : tree.jobs.filter((job) => job.type !== type)
    onChange({ ...tree, jobs })
  }

  const isInspector = variant === 'inspector'
  const typedMinutes = parseMinutes(minutesText)

  /*
   * Archived species are offered only when this tree already wears one.
   * Retiring a species should stop it being chosen again without stranding the
   * trees that have it — and without making a re-identification the only way to
   * edit one of them.
   */
  const speciesChoices = species.filter(
    (choice) => choice.archivedAt === null || choice.id === tree.speciesId,
  )

  /**
   * Commit a typed estimate, or clear it.
   *
   * An empty field means "not estimated yet" — null, not zero. Zero would mean
   * this tree needs no work at all, which is a different and far more expensive
   * claim for a quote to carry.
   */
  function commitMinutes(raw: string): void {
    setMinutesText(raw)
    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange({ ...tree, estimatedLaborMinutes: null })
      return
    }
    const parsed = parseMinutes(trimmed)
    if (parsed === null) return
    onChange({ ...tree, estimatedLaborMinutes: parsed })
  }

  /**
   * The line under the field: an estimate over an hour spelled out, the reason
   * it cannot be read, or a reminder that leaving it empty is allowed.
   *
   * One line that always says something, rather than a message that appears
   * and disappears and shifts the fields below it as someone types.
   */
  function renderMinutesReadback(): string {
    if (minutesText.trim() === '') {
      return 'Leave blank if it has not been estimated yet.'
    }
    if (typedMinutes === null) {
      return `Whole minutes, up to ${String(MAX_ESTIMATED_MINUTES)}.`
    }
    /*
     * Under an hour there is nothing to translate — "45 minutes" said back at
     * someone who typed 45 is noise. Past it, the hours-and-minutes reading is
     * how a crew day is actually planned, so 225 is worth showing as 3h 45m.
     */
    if (typedMinutes < 60) {
      return 'Minutes of crew time for this tree.'
    }
    return formatMinutesLong(typedMinutes) ?? ''
  }

  /**
   * Set one job's minutes, or clear the split entirely.
   *
   * Clearing a single job would leave the tree half-itemized, which the API
   * refuses — the parts have to add up to the whole. So an emptied field
   * clears every job's minutes, turning the tree back into one estimate the
   * arborist types themselves. That is a real thing to want: itemizing is
   * optional, and undoing it should not mean deleting jobs.
   */
  function setJobMinutes(type: JobType, raw: string): void {
    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange({
        ...tree,
        jobs: tree.jobs.map((job) => ({ ...job, minutes: null })),
      })
      return
    }
    const minutes = parseMinutes(trimmed)
    if (minutes === null) return
    onChange({
      ...tree,
      /*
       * Only the job that was typed into. Filling the others with zero would
       * "complete" the split without the arborist having thought about them,
       * and a job sitting at zero minutes is a job the client can decline for
       * no money — which reads as agreement that it is worth nothing. The
       * others stay empty and the warning stands until they are filled.
       */
      jobs: tree.jobs.map((job) =>
        job.type === type ? { ...job, minutes } : job,
      ),
    })
  }

  function setJobNotes(type: JobType, notes: string): void {
    onChange({
      ...tree,
      jobs: tree.jobs.map((job) =>
        job.type === type ? { ...job, notes: notes === '' ? null : notes } : job,
      ),
    })
  }

  function setJobLevel(type: JobType, level: JobLevel): void {
    onChange({
      ...tree,
      jobs: tree.jobs.map((job) =>
        job.type === type ? { ...job, level } : job,
      ),
    })
  }

  return (
    <div
      className={cn(
        /*
         * `scrollbar-gutter: stable` and an explicit `overflow-x` are both
         * load-bearing, and they fix the two halves of the same bug.
         *
         * Adding a photo makes this panel taller. On a browser with classic
         * scrollbars that can push it past its own max-height, and the
         * scrollbar that appears takes 15px out of the CONTENT box — so every
         * field in the form shifts sideways the instant a thumbnail lands.
         * `stable` reserves that space whether or not the bar is showing, so
         * the width never changes. It costs nothing on a phone, where overlay
         * scrollbars make the gutter zero.
         *
         * The second half is why the shift was not merely cosmetic. CSS does
         * not let the two axes disagree about whether they scroll: setting
         * `overflow-y` to `auto` while leaving `overflow-x` at `visible`
         * computes the x axis to `auto` too. So anything that fit exactly
         * before the 15px was taken away now overflows, and the whole form
         * becomes a sideways-scrolling surface that has to be dragged back.
         * A form is meant to fit its column; pinning the axis says so.
         */
        'pointer-events-auto overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] overscroll-contain bg-surface',
        isInspector
          ? // A column beside the map: full height, and separated by a border
            // rather than a shadow, since it sits flush against the map rather
            // than floating over it.
            'h-full border-l border-default'
          : 'max-h-[68svh] rounded-t-lg border-t border-default pb-[env(safe-area-inset-bottom)] shadow-dialog',
      )}
    >
      <div className="sticky top-0 flex flex-wrap items-center justify-between gap-x-2 border-b border-default bg-surface px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <TreeGlyph species={tree.species} size={18} />
          <span className="truncate text-base font-semibold">{tree.label}</span>
          <span className="truncate text-sm text-muted-text">
            {tree.species?.name ?? UNIDENTIFIED_LABEL}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <TreePriceFigure price={price} />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={onClose}
            aria-label="Close tree details"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <TreePriceNotes price={price} />
      </div>

      <div className="space-y-4 px-3 py-3">
        {outsideBoundary ? (
          <div className="flex items-start gap-2 rounded-md border border-default bg-warning-subtle px-2.5 py-2">
            <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-warning" />
            <p className="text-xs leading-snug">
              This tree is outside the site boundary. Move the pin if the
              position is wrong — or leave it, if the tree really is off the
              site.
            </p>
          </div>
        ) : null}

        <fieldset disabled={!canEditRecord} className={RECORD}>
          {canEditRecord ? null : (
            <p className="rounded-md bg-subtle px-2.5 py-2 text-xs text-secondary-text">
              Shown as it was on this visit. What the tree is, its label,
              species, diameter, notes and pin, is changed on the newest visit.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="tree-label">Label</Label>
            <Input
              id="tree-label"
              value={tree.label}
              maxLength={12}
              className={FIELD_CONTROL}
              onChange={(event) => {
                onChange({ ...tree, label: event.target.value })
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Species</Label>
            <div
              className={cn(
                'grid gap-1.5',
                /*
                 * ONE column on a phone.
                 *
                 * Species names are typed by the business now, and real ones are
                 * long — "Southern live oak (Quercus virginiana)" is an ordinary
                 * entry. Two columns on a 375px iPhone gives each chip about
                 * 160px, which truncated every name past about fifteen
                 * characters and left a surveyor choosing between "Southern live
                 * oak…" and "Southern red oak…".
                 *
                 * Full width costs a little scrolling and buys a name somebody
                 * can actually read. Two columns return at `sm`, where there is
                 * room for both.
                 */
                isInspector ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
              )}
            >
              {speciesChoices.map((choice) => {
                const active = tree.speciesId === choice.id
                return (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => {
                      setSpecies(choice)
                    }}
                    className={cn(
                      'flex items-center gap-2 rounded-md border px-2.5 text-left text-sm transition-colors',
                      FIELD_CONTROL,
                      active
                        ? 'border-brand bg-brand-subtle font-medium'
                        : 'border-default bg-surface hover:bg-subtle',
                    )}
                  >
                    <TreeGlyph
                      species={{
                        name: choice.name,
                        shape: choice.shape,
                        color: choice.color,
                      }}
                      size={16}
                    />
                    <span className="min-w-0 truncate">{choice.name}</span>
                  </button>
                )
              })}
              {/*
                "Not identified" is last and always present. It is the honest
                answer from under a bare winter canopy, and it is what makes the
                survey a work list rather than a pile of guesses — so it is a
                choice somebody can return to, not just the state they start in.
              */}
              <button
                type="button"
                onClick={() => {
                  setSpecies(null)
                }}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-2.5 text-left text-sm transition-colors',
                  FIELD_CONTROL,
                  tree.speciesId === null
                    ? 'border-brand bg-brand-subtle font-medium'
                    : 'border-default bg-surface hover:bg-subtle',
                )}
              >
                <TreeGlyph species={null} size={16} />
                <span className="truncate">{UNIDENTIFIED_LABEL}</span>
              </button>
            </div>
          </div>
        </fieldset>

        {canRecordFindings && lastVisit !== null ? (
          <TreeLastVisit
            lastVisit={lastVisit}
            problems={tree.problems}
            problemTypes={problemTypes}
            controlClassName={FIELD_CONTROL}
            onChange={(problems) => {
              onChange({ ...tree, problems })
            }}
          />
        ) : null}

        {/*
          What was found and recommended is the visit's; a fieldset disables
          every control in it at once, so none can slip through unlocked.
        */}
        <fieldset disabled={!canRecordFindings} className={FINDINGS}>
          {canRecordFindings ? null : (
            <p className="rounded-md bg-subtle px-2.5 py-2 text-xs text-secondary-text">
              {findingsNote(findingsLoading, canEditRecord)}
            </p>
          )}
          {hazardWaits(tree, currentSeason) ? (
            <div className="flex items-start gap-2 rounded-md border border-default bg-warning-subtle px-2.5 py-2">
              <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-warning" />
              <p className="text-xs leading-snug">
                A problem here is rated serious or critical, but some of its
                work is set for a later season. The client only approves the
                next visit, so that work would wait.
              </p>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label>Pruning job types</Label>
            {/*
              An answer, not a blank. A tree with no jobs and no time is one
              nobody has assessed, which stops a proposal being sent; this is
              the arborist saying they looked and it needs none, which the
              proposal prints.
            */}
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={tree.noPruningRecommended}
                onChange={(event) => {
                  setNoPruning(event.target.checked)
                }}
              />
              No pruning recommended
            </label>
            {tree.noPruningRecommended ? (
              <p className="text-xs text-muted-text">
                The proposal says so for this tree. Problems and a removal can
                still be quoted below.
              </p>
            ) : (
              <div className="space-y-1.5">
                {JOB_TYPES.map((type) => {
                  const job = tree.jobs.find((candidate) => candidate.type === type)
                  const active = job !== undefined
                  return (
                    <div
                      key={type}
                      className={cn(
                        'flex items-center gap-2 rounded-md border pr-1.5 pl-2.5',
                        FIELD_CONTROL,
                        active
                          ? 'border-brand bg-brand-subtle'
                          : 'border-default bg-surface',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          toggleJob(type)
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left"
                        aria-pressed={active}
                      >
                        <span
                          className={cn(
                            'h-3 w-3 shrink-0 rounded-[2px]',
                            active ? JOB_TYPE_BG_CLASSES[type] : 'bg-muted',
                          )}
                        />
                        <span
                          className={cn(
                            'truncate text-sm',
                            active ? 'font-medium' : 'text-secondary-text',
                          )}
                        >
                          {JOB_TYPE_LABELS[type]}
                        </span>
                      </button>
                      {job !== undefined && jobTypeHasLevel(type) ? (
                        /*
                         * The level picker has to sit INSIDE the row, not fill it.
                         *
                         * Its height is the button plus this wrapper's padding, so
                         * it has to be sized against the row at each breakpoint: a
                         * 32px button in a 4px wrapper is exactly the 36px desktop
                         * row, which puts its corners on top of the row's border
                         * and makes a selected level look like it has escaped the
                         * box. One size smaller at each width leaves 2px of the
                         * row visible above and below.
                         */
                        <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-surface p-0.5">
                          {JOB_LEVELS.map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => {
                                setJobLevel(type, level)
                              }}
                              className={cn(
                                'h-8 w-8 rounded-[4px] text-xs font-semibold transition-colors lg:h-7 lg:w-7',
                                job.level === level
                                  ? 'bg-brand text-primary-foreground'
                                  : 'text-muted-text hover:bg-subtle',
                              )}
                              aria-label={`Level ${String(level)}`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {job === undefined ? null : (
                        <Input
                          inputMode="numeric"
                          aria-label={`Minutes for ${JOB_TYPE_LABELS[type]}`}
                          placeholder="min"
                          className="h-8 w-16 shrink-0 text-center lg:h-7"
                          value={job.minutes?.toString() ?? ''}
                          onChange={(event) => {
                            setJobMinutes(type, event.target.value)
                          }}
                        />
                      )}
                    </div>
                  )
                })}
                {/*
                  The rule, said where it is being broken rather than in a tooltip.
                  Itemizing is optional, but it is all-or-nothing per tree: half a
                  split means the parts do not add up to the whole, and a client
                  declining one job would take away a figure that was never part
                  of what they were charged.
                */}
                {tree.jobs.length > 0 && partiallyItemized ? (
                  <p className="text-xs text-warning">
                    Give every job a time, or clear them all — a half-split tree
                    cannot be saved.
                  </p>
                ) : null}
                {/*
                  The operator's rule, said on the form rather than discovered by
                  a save that never lands. The tree stays on the device either way;
                  this is what tells somebody why.
                */}
                {requireItemizedEstimates &&
                tree.jobs.length > 0 &&
                !someItemized ? (
                  <p className="text-xs text-warning">
                    Every pruning job needs a time before this tree can be saved.
                  </p>
                ) : null}
                {otherJob === undefined ? null : (
                  <div className="space-y-1.5">
                    <Label htmlFor="job-other-notes">What is the other work?</Label>
                    <Input
                      id="job-other-notes"
                      className={FIELD_CONTROL}
                      placeholder="Traffic control on the entrance drive…"
                      value={otherJob.notes ?? ''}
                      onChange={(event) => {
                        setJobNotes('OTHER', event.target.value)
                      }}
                    />
                    {/*
                      This is the one line a client cannot decline, so it has to
                      say what it is. An unlabelled charge nobody may argue with is
                      the worst thing to put in front of a board.
                    */}
                    {(otherJob.minutes ?? 0) > 0 && otherJob.notes === null ? (
                      <p className="text-xs text-danger">
                        Required — the client cannot decline this one, so it has to
                        say what it covers.
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/*
            One season for all of the tree's pruning, because its jobs are done
            on one visit. Removal goes with it: it is what the client weighs
            against the pruning, and in different seasons they could not.
          */}
          {tree.noPruningRecommended && !tree.removalOffered ? null : (
            <div className="space-y-1.5">
              <Label>Pruning season</Label>
              <SeasonPicker
                value={tree.pruningSeason}
                current={currentSeason}
                label="Pruning season"
                controlClassName={FIELD_CONTROL}
                onChange={(season) => {
                  onChange({ ...tree, pruningSeason: season })
                }}
              />
              <p className="text-xs text-muted-text">
                {tree.pruningSeason === null && hasPruningWork(tree)
                  ? 'No season chosen, so it goes with the next visit.'
                  : 'When this tree is pruned. A removal, if quoted, goes with it.'}
              </p>
            </div>
          )}

          {tree.noPruningRecommended ? null : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="tree-minutes">Estimated labor</Label>
                <div className="relative">
                  <Input
                    id="tree-minutes"
                    /*
                     * Read-only once the tree is itemized, because then the estimate
                     * IS the sum of the jobs rather than a second opinion about it.
                     * Two numbers that can disagree eventually will, and the one a
                     * client was charged against would be whichever the renderer
                     * happened to read.
                     */
                    readOnly={itemizedTotal !== null}
                    value={
                      itemizedTotal === null ? minutesText : String(itemizedTotal)
                    }
                    // `numeric`, not `decimal`: minutes are whole, so a phone should
                    // offer a keypad with no decimal point on it at all.
                    inputMode="numeric"
                    placeholder="45"
                    aria-invalid={minutesText.trim() !== '' && typedMinutes === null}
                    aria-describedby="tree-minutes-readback"
                    className={cn(FIELD_CONTROL, 'pr-16')}
                    onChange={(event) => {
                      commitMinutes(event.target.value)
                    }}
                  />
                  <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-text">
                    minutes
                  </span>
                </div>
                <p
                  id="tree-minutes-readback"
                  className={cn(
                    'text-xs',
                    minutesText.trim() !== '' && typedMinutes === null
                      ? 'text-danger'
                      : 'text-muted-text',
                  )}
                >
                  {itemizedTotal === null
                    ? renderMinutesReadback()
                    : 'Added up from the job times above.'}
                </p>
              </div>

              {/*
                The price the time works out to, said where the time is typed,
                with a way to set it by hand for the tree the formula gets wrong:
                a lift, a power line, a yard with no gate.
              */}
              <PriceOverride
                id="tree-pruning-price"
                label="Pruning price"
                formulaCents={pruningFormulaCents}
                formulaBasis="at the crew rate"
                unpricedNote={unpricedNote(pruningMinutes)}
                value={tree.pruningPriceOverride}
                controlClassName={FIELD_CONTROL}
                onChange={(price) => {
                  onChange({ ...tree, pruningPriceOverride: price })
                }}
              />
            </>
          )}

          {/*
            The arithmetic, said out loud.

            Treatment minutes ADD to the estimate rather than replacing it, which
            is easy to record twice: the minutes somebody logs for a tree often
            already include the pruning they then pick as a solution. Showing
            both numbers and their sum puts the overlap in front of the one
            person who can tell whether it is real — while they are still under
            the tree.
          */}
          {solutionMinutesTotal === 0 ? null : (
            <div className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
              <div className="flex items-center justify-between gap-2 tabular-nums">
                <span>Estimated labor</span>
                <span>{formatMinutesShort(tree.estimatedLaborMinutes ?? 0)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 tabular-nums">
                <span>Treatments</span>
                <span>+ {formatMinutesShort(solutionMinutesTotal)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 border-t border-default pt-1 font-medium tabular-nums text-primary-text">
                <span>Crew time on this tree</span>
                <span>
                  {formatMinutesShort(
                    (tree.estimatedLaborMinutes ?? 0) + solutionMinutesTotal,
                  )}
                </span>
              </div>
              <p className="mt-1 text-muted-text">
                Treatments are added to the estimate. If the estimate already
                covered them, bring it down.
              </p>
            </div>
          )}

          {/*
            Removal sits after pruning and reads as an alternative to it, because
            that is what it is: a client choosing removal is choosing INSTEAD of
            pruning, and instead of treating the tree's problems. Quoting both
            lets them weigh the two.
          */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={tree.removalOffered}
                onChange={(event) => {
                  setRemovalOffered(event.target.checked)
                }}
              />
              Quote removal for this tree
            </label>
            {tree.removalOffered ? (
              <div className="space-y-1.5 rounded-md border border-default bg-subtle p-2.5">
                <Label htmlFor="tree-removal-minutes">Removal time</Label>
                <div className="relative">
                  <Input
                    id="tree-removal-minutes"
                    inputMode="numeric"
                    placeholder="240"
                    className={cn(FIELD_CONTROL, 'pr-16')}
                    value={removalMinutesText}
                    onChange={(event) => {
                      commitRemovalMinutes(event.target.value)
                    }}
                  />
                  <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-text">
                    minutes
                  </span>
                </div>
                <PriceOverride
                  id="tree-removal-price"
                  label="Removal price"
                  formulaCents={removalFormulaCents}
                  formulaBasis="at the crew rate"
                  unpricedNote={unpricedNote(tree.removalMinutes)}
                  value={tree.removalPriceOverride}
                  controlClassName={FIELD_CONTROL}
                  onChange={(price) => {
                    onChange({ ...tree, removalPriceOverride: price })
                  }}
                />
                <Label htmlFor="tree-removal-notes">
                  Why removal? (shown to the client)
                </Label>
                <Input
                  id="tree-removal-notes"
                  className={FIELD_CONTROL}
                  placeholder="Declining trunk, overhangs the car park…"
                  value={tree.removalNotes ?? ''}
                  onChange={(event) => {
                    const value = event.target.value
                    onChange({
                      ...tree,
                      removalNotes: value === '' ? null : value,
                    })
                  }}
                />
                <p className="text-xs text-muted-text">
                  Quoted separately from the pruning above. The client picks one
                  or the other, not both.
                </p>
              </div>
            ) : null}
          </div>

          {/*
            Beside removal because it is the same kind of decision: not what the
            work is, but how the proposal offers it. A proposal bids everything
            worth doing, and this is how the arborist says which part is the
            recommendation and which is the extra a board can take or leave.
          */}
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={tree.isOptional}
                onChange={(event) => {
                  onChange({ ...tree, isOptional: event.target.checked })
                }}
              />
              Optional work
            </label>
            <p className="text-xs text-muted-text">
              Offered, but not part of what we recommend. Proposals keep it out
              of “Everything recommended”.
            </p>
          </div>
        </fieldset>

        <fieldset disabled={!canEditRecord} className={RECORD}>
          <div className="space-y-1.5">
            <Label htmlFor="tree-dbh">Trunk diameter (DBH)</Label>
            <div className="relative">
              <Input
                id="tree-dbh"
                aria-invalid={dbhIsUnreadable}
                value={dbhText}
                inputMode="decimal"
                placeholder="14.5"
                className={cn(FIELD_CONTROL, 'pr-16')}
                onChange={(event) => {
                  commitDbh(event.target.value)
                }}
              />
              <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-text">
                inches
              </span>
            </div>
            <p
              className={cn(
                'text-xs',
                dbhIsUnreadable ? 'text-danger' : 'text-muted-text',
              )}
            >
              {dbhIsUnreadable
                ? 'That cannot be read as a measurement — try 14 or 14.5.'
                : 'Measured once, here. Treatments charged by diameter read it, so nobody types it again.'}
            </p>
          </div>
        </fieldset>

        <fieldset disabled={!canRecordFindings} className={FINDINGS}>
          {/*
            Problems, above notes and visually distinct from them.

            A list rather than free text, because the map reads it: a pin
            carrying a problem mark is what lets somebody scan a site for the
            trees needing a second look, which no amount of prose in a notes
            column can do. And a list rather than one flag, because a tree can
            have two things wrong with it that deserve different answers.
          */}
          <TreeProblems
            problems={tree.problems}
            types={problemTypes}
            dbhInches={tree.dbhInches}
            ratePerHour={ratePerHour}
            seasons={{
              current: currentSeason,
              fallback: tree.pruningSeason ?? currentSeason,
            }}
            isOnline={isOnline}
            isPendingSync={isPendingSync}
            controlClassName={FIELD_CONTROL}
            onChange={(problems) => {
              onChange({ ...tree, problems })
            }}
          />

          {/*
            Why a price was set by hand, for whoever reads the quote back in a
            month. Shown only while one is, and never on the proposal.
          */}
          {hasPriceOverride(tree) ? (
            <div className="space-y-1.5">
              <Label htmlFor="tree-price-notes">
                Why was the price set by hand?
              </Label>
              <Input
                id="tree-price-notes"
                className={FIELD_CONTROL}
                placeholder="Needs a lift; power line through the crown…"
                value={tree.priceOverrideNotes ?? ''}
                onChange={(event) => {
                  const value = event.target.value
                  onChange({
                    ...tree,
                    priceOverrideNotes: value === '' ? null : value,
                  })
                }}
              />
              <p className="text-xs text-muted-text">
                For the office. Never shown to the client.
              </p>
            </div>
          ) : null}

          {/*
            The tree's OWN photographs, after its problems. Each problem carries
            its own camera, so what lands here is what the whole tree looks like
            — canopy shape, access for equipment, where the trunk sits — rather
            than the evidence for a particular finding.
          */}
          <SurveyPhotos
            scope={{ treeId: tree.id, visitId }}
            isOnline={isOnline}
            isPendingSync={isPendingSync}
            controlClassName={FIELD_CONTROL}
          />

          {/*
            Video is optional infrastructure — a deployment without Cloudflare
            Stream configured should not show a button that answers 503. Asked
            once per session; an unconfigured capability looks absent rather than
            broken.
          */}
          {videoAvailability.data === true ? (
            <SurveyVideos
              owner={{ treeId: tree.id }}
              visitId={visitId}
              isOnline={isOnline}
              isPendingSync={isPendingSync}
              controlClassName={FIELD_CONTROL}
            />
          ) : null}
        </fieldset>

        <fieldset disabled={!canEditRecord} className={RECORD}>
          <div className="space-y-1.5">
            <Label htmlFor="tree-notes">Notes</Label>
            <Input
              id="tree-notes"
              value={tree.notes ?? ''}
              placeholder="Deadwood over the path, cable in canopy…"
              className={FIELD_CONTROL}
              onChange={(event) => {
                // Empty means "no note", not an empty note — the column is
                // nullable and a blank string would make "has notes" filters lie.
                const value = event.target.value
                onChange({ ...tree, notes: value === '' ? null : value })
              }}
            />
          </div>
        </fieldset>

        {/* What each visit found on it, its changes and work done. */}
        <RecordHistory
          siteId={tree.siteId}
          entity="TREE"
          recordId={tree.id}
          controlClassName={FIELD_CONTROL}
        />

        {/* Moving or deleting the tree changes the site, as it stands now. */}
        {canEditRecord ? (
            <div className="flex items-center gap-2 border-t border-default pt-3">
              {/*
                On a revisit deleting asks why, in two buttons, which with Cancel
                leave no room for moving the pin beside them.
              */}
              {confirmingDelete && removeQuestion !== null ? null : (
                <Button
                  variant="outline"
                  className={cn('flex-1', FIELD_CONTROL)}
                  onClick={onMove}
                >
                  <Crosshair className="h-4 w-4" />
                  Move pin
                </Button>
              )}
              {confirmingDelete ? (
                <>
                  {removeQuestion === null ? (
                    <Button
                      variant="destructive"
                      className={FIELD_CONTROL}
                      onClick={() => {
                        onDelete()
                      }}
                    >
                      Delete
                    </Button>
                  ) : (
                    <ChangeAnswers
                      question={removeQuestion}
                      tone="remove"
                      buttonClassName={FIELD_CONTROL}
                      askWhen={asksWhen}
                      onAnswer={onDelete}
                    />
                  )}
                  <Button
                    variant="ghost"
                    className={FIELD_CONTROL}
                    onClick={() => {
                      setConfirmingDelete(false)
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('w-11 text-danger', FIELD_CONTROL)}
                  onClick={() => {
                    setConfirmingDelete(true)
                  }}
                  aria-label="Delete tree"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
        ) : null}

        {/*
          A second way out, at the end of the form.
          
          The close button in the header is the only other one, and by the time
          somebody has worked down through species, jobs, an estimate and notes
          it is several screens above their thumb — so finishing a tree meant
          scrolling all the way back up to dismiss a form they had already
          finished with.

          It is not sticky: the sheet is capped at 68svh on a phone and a fixed
          footer would spend some of the little height left on a control that
          is only wanted once. At the end of the content is exactly where the
          thumb already is when the last field is filled.
        */}
        <Button
          variant="outline"
          className={cn('w-full', FIELD_CONTROL)}
          onClick={onClose}
        >
          <Check className="h-4 w-4" />
          Done
        </Button>
      </div>
    </div>
  )
}
